import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

const FIVE_MIN = 5 * 60 * 1000;

export interface B2BKpis {
  sessions_a_venir: number;
  sessions_en_cours: number;
  formateurs_actifs: number;
  taux_remplissage_avg: number;
  factures_en_attente: number;
  factures_encaisse_30j: number;
  factures_en_retard: number;
  dettes_total: number;
  dettes_semaine: number;
  ca_realise_30j: number;
  benefice_net_30j: number;
}

export function useFormationB2BKpis() {
  return useQuery({
    queryKey: ['formation_b2b_kpis'],
    queryFn: async (): Promise<B2BKpis | null> => {
      const { data, error } = await supabase.from('v_formation_b2b_kpis').select('*').maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as Record<string, unknown>;
      return {
        sessions_a_venir: Number(r.sessions_a_venir ?? 0),
        sessions_en_cours: Number(r.sessions_en_cours ?? 0),
        formateurs_actifs: Number(r.formateurs_actifs ?? 0),
        taux_remplissage_avg: Number(r.taux_remplissage_avg ?? 0),
        factures_en_attente: Number(r.factures_en_attente ?? 0),
        factures_encaisse_30j: Number(r.factures_encaisse_30j ?? 0),
        factures_en_retard: Number(r.factures_en_retard ?? 0),
        dettes_total: Number(r.dettes_total ?? 0),
        dettes_semaine: Number(r.dettes_semaine ?? 0),
        ca_realise_30j: Number(r.ca_realise_30j ?? 0),
        benefice_net_30j: Number(r.benefice_net_30j ?? 0),
      };
    },
    staleTime: FIVE_MIN,
  });
}

export interface B2BSession {
  id: string;
  reference: string;
  formation_id: string | null;
  formation_titre: string | null;
  formation_code: string | null;
  formateur_principal_id: string | null;
  formateur_nom: string | null;
  formateur_prenom: string | null;
  date_debut: string | null;
  date_fin: string | null;
  lieu_adresse: string | null;
  lieu_type: string | null;
  capacite_min: number | null;
  capacite_max: number;
  statut: string;
  session_type: string;
  prix_session: number | null;
  nb_inscrits: number;
  dossier_id: string | null;
}

export function useFormationB2BSessions() {
  return useQuery({
    queryKey: ['formation_b2b_sessions'],
    queryFn: async (): Promise<B2BSession[]> => {
      const { data, error } = await supabase
        .from('v_formation_b2b_sessions')
        .select('*')
        .order('date_debut', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as B2BSession[];
    },
  });
}

export interface B2BFacture {
  id: string;
  reference: string;
  facture_ref: string | null;
  tier_id: string;
  client_nom: string | null;
  date_emission: string;
  date_echeance: string;
  montant_ht: number | null;
  montant_ttc: number | null;
  montant_du: number;
  montant_paye: number;
  tva_taux: number;
  statut: string;
  closed_at: string | null;
  dossier_id: string | null;
}

export function useFormationB2BFactures() {
  return useQuery({
    queryKey: ['formation_b2b_factures'],
    queryFn: async (): Promise<B2BFacture[]> => {
      const { data, error } = await supabase
        .from('v_formation_b2b_factures')
        .select('*')
        .order('date_echeance', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as B2BFacture[];
    },
  });
}

export interface B2BDette {
  id: string;
  source: 'echeance' | 'dossier_paiement';
  source_id: string;
  reference: string;
  fournisseur_nom: string | null;
  fournisseur_type: string | null;
  dossier_id: string | null;
  date_facture: string | null;
  date_echeance: string | null;
  montant: number;
  montant_du: number;
  montant_paye: number;
  statut: string;
  justificatif_url: string | null;
}

export function useFormationB2BDettes() {
  return useQuery({
    queryKey: ['formation_b2b_dettes'],
    queryFn: async (): Promise<B2BDette[]> => {
      const { data, error } = await supabase
        .from('v_formation_b2b_dettes')
        .select('*')
        .order('date_echeance', { ascending: true, nullsFirst: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as B2BDette[];
    },
  });
}

export interface B2BRoi {
  dossier_id: string;
  dossier_reference: string;
  titre: string;
  session_id: string | null;
  session_reference: string | null;
  client_tier_id: string | null;
  client_nom: string | null;
  formateur_id: string | null;
  formateur_nom: string | null;
  formateur_prenom: string | null;
  date_debut: string | null;
  date_fin: string | null;
  prix_vente_ht: number;
  prix_vente_ttc: number;
  statut_global: string;
  total_charges: number;
  benefice_brut: number;
  marge_pct: number;
}

export function useFormationB2BRoi() {
  return useQuery({
    queryKey: ['formation_b2b_roi'],
    queryFn: async (): Promise<B2BRoi[]> => {
      const { data, error } = await supabase
        .from('v_formation_b2b_roi')
        .select('*')
        .order('marge_pct', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as B2BRoi[];
    },
  });
}

export interface B2BInterSession {
  id: string;
  reference: string;
  formation_id: string | null;
  formation_titre: string | null;
  date_debut: string;
  date_fin: string;
  lieu_adresse: string | null;
  capacite_max: number;
  statut: string;
  nb_entreprises: number;
  nb_participants: number;
  ca_cumule: number;
}

export function useFormationB2BInter() {
  return useQuery({
    queryKey: ['formation_b2b_inter'],
    queryFn: async (): Promise<B2BInterSession[]> => {
      const { data, error } = await supabase
        .from('v_formation_b2b_sessions_inter')
        .select('*')
        .order('date_debut', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as B2BInterSession[];
    },
  });
}
